/**
 * ETIQUETAS DEL ESTÁNDAR IFC en español — el ÚNICO archivo de etiquetas IFC del repo.
 *
 * Las clases de elemento de IFC (ISO 16739: IfcWall, IfcSlab, IfcDoor…) son un conjunto FINITO que fija
 * el esquema, no el cliente ni el catálogo. Traducirlas es texto de interfaz, no datos de obra: es la
 * ÚNICA tabla fija autorizada por Sebastián (2026-09-23, ver memoria
 * feedback_no_hardcoded_without_validation). Si aparece una clase no listada, se muestra su nombre sin
 * el prefijo "Ifc" como último recurso. Nada aquí mira el NOMBRE del tipo (que viene en el idioma o en
 * los códigos del programa que lo exportó): todo sale de la clase, de propiedades estándar
 * (PredefinedType, IsExternal de Pset_*Common) y de lo MEDIDO.
 *
 * Todo lo que la pantalla y la sugerencia de conceptos dicen de un tipo IFC sale de aquí, para que
 * "Muro exterior 30 cm" en la lista y lo que se le cuenta a la IA sean el mismo dato.
 */
import type { DecisionDeCapa, PlanoCapa } from './domain.js';

/** Grupos de la lista "Lo que trae el modelo", en el orden en que se muestran. */
export const GRUPOS_IFC = [
  'Muros',
  'Losas',
  'Techo',
  'Estructura',
  'Refuerzo',
  'Puertas y ventanas',
  'Escaleras y rampas',
  'Instalaciones',
  'Infraestructura',
  'Otros',
] as const;
export type GrupoIfc = (typeof GRUPOS_IFC)[number];

export type MagnitudIfc = 'area' | 'longitud' | 'volumen' | 'conteo';

/**
 * Lo que la interfaz sabe de una clase del estándar: SOLO su nombre en español y su grupo. Por qué
 * magnitud se mide un tipo (m, m², m³ o pieza) NO sale de aquí: lo decide el visor con la forma de sus
 * piezas y lo que el modelo dice de ellas (`medidas.manda`, ver apps/web/src/lib/medicionIfc.ts).
 */
interface ClaseIfcInfo {
  plural: string;
  singular: string;
  grupo?: GrupoIfc;
}

/**
 * La tabla de clases del estándar. Incluye clases ABSTRACTAS (IfcDistributionElement,
 * IfcReinforcingElement, IfcFlowSegment…): una clase concreta que no está aquí hereda el grupo y las
 * magnitudes de su ancestro más cercano que sí esté (la herencia la dice el esquema, `supertipos`). Así
 * un IfcCableCarrierSegment de IFC4 cae en "Instalaciones" y se mide por largo sin nombrarlo.
 */
const CLASES_IFC_INFO: Readonly<Record<string, ClaseIfcInfo>> = {
  // --- Muros ---
  IfcWall: { plural: 'Muros', singular: 'Muro', grupo: 'Muros' },
  IfcWallStandardCase: { plural: 'Muros', singular: 'Muro', grupo: 'Muros' },
  IfcWallElementedCase: { plural: 'Muros', singular: 'Muro', grupo: 'Muros' },
  IfcCurtainWall: { plural: 'Muro cortina', singular: 'Muro cortina', grupo: 'Muros' },
  // --- Losas y techo ---
  IfcSlab: { plural: 'Losas y pisos', singular: 'Losa', grupo: 'Losas' },
  IfcSlabStandardCase: { plural: 'Losas y pisos', singular: 'Losa', grupo: 'Losas' },
  IfcSlabElementedCase: { plural: 'Losas y pisos', singular: 'Losa', grupo: 'Losas' },
  IfcRoof: { plural: 'Cubiertas', singular: 'Cubierta', grupo: 'Techo' },
  IfcCovering: { plural: 'Acabados y recubrimientos', singular: 'Acabado', grupo: 'Otros' },
  // --- Estructura (y cimentación) ---
  IfcColumn: { plural: 'Columnas', singular: 'Columna', grupo: 'Estructura' },
  IfcColumnStandardCase: { plural: 'Columnas', singular: 'Columna', grupo: 'Estructura' },
  IfcBeam: { plural: 'Vigas', singular: 'Viga', grupo: 'Estructura' },
  IfcBeamStandardCase: { plural: 'Vigas', singular: 'Viga', grupo: 'Estructura' },
  IfcMember: { plural: 'Elementos estructurales', singular: 'Elemento estructural', grupo: 'Estructura' },
  IfcMemberStandardCase: { plural: 'Elementos estructurales', singular: 'Elemento estructural', grupo: 'Estructura' },
  IfcPlate: { plural: 'Placas y cristales', singular: 'Placa', grupo: 'Estructura' },
  IfcPlateStandardCase: { plural: 'Placas y cristales', singular: 'Placa', grupo: 'Estructura' },
  IfcFooting: { plural: 'Cimentación', singular: 'Cimiento', grupo: 'Estructura' },
  IfcPile: { plural: 'Pilotes', singular: 'Pilote', grupo: 'Estructura' },
  IfcCaissonFoundation: { plural: 'Cajones de cimentación', singular: 'Cajón de cimentación', grupo: 'Estructura' },
  IfcChimney: { plural: 'Chimeneas', singular: 'Chimenea', grupo: 'Otros' },
  IfcShadingDevice: { plural: 'Parasoles', singular: 'Parasol', grupo: 'Otros' },
  IfcMechanicalFastener: { plural: 'Uniones', singular: 'Unión', grupo: 'Estructura' },
  IfcFastener: { plural: 'Uniones', singular: 'Unión', grupo: 'Estructura' },
  IfcDiscreteAccessory: { plural: 'Accesorios y uniones', singular: 'Accesorio', grupo: 'Estructura' },
  IfcElementAssembly: { plural: 'Conjuntos', singular: 'Conjunto', grupo: 'Otros' },
  // --- Refuerzo (IfcReinforcingElement y sus hijas) ---
  IfcReinforcingElement: { plural: 'Refuerzo', singular: 'Refuerzo', grupo: 'Refuerzo' },
  IfcReinforcingBar: { plural: 'Varillas', singular: 'Varilla', grupo: 'Refuerzo' },
  IfcReinforcingMesh: { plural: 'Mallas de refuerzo', singular: 'Malla', grupo: 'Refuerzo' },
  IfcTendon: { plural: 'Cables de presfuerzo', singular: 'Cable de presfuerzo', grupo: 'Refuerzo' },
  IfcTendonAnchor: { plural: 'Anclajes de presfuerzo', singular: 'Anclaje de presfuerzo', grupo: 'Refuerzo' },
  // --- Puertas y ventanas ---
  IfcDoor: { plural: 'Puertas', singular: 'Puerta', grupo: 'Puertas y ventanas' },
  IfcDoorStandardCase: { plural: 'Puertas', singular: 'Puerta', grupo: 'Puertas y ventanas' },
  IfcWindow: { plural: 'Ventanas', singular: 'Ventana', grupo: 'Puertas y ventanas' },
  IfcWindowStandardCase: { plural: 'Ventanas', singular: 'Ventana', grupo: 'Puertas y ventanas' },
  // --- Escaleras, rampas y barandales ---
  IfcStair: { plural: 'Escaleras', singular: 'Escalera', grupo: 'Escaleras y rampas' },
  IfcStairFlight: { plural: 'Tramos de escalera', singular: 'Tramo de escalera', grupo: 'Escaleras y rampas' },
  IfcRamp: { plural: 'Rampas', singular: 'Rampa', grupo: 'Escaleras y rampas' },
  IfcRampFlight: { plural: 'Tramos de rampa', singular: 'Tramo de rampa', grupo: 'Escaleras y rampas' },
  IfcRailing: { plural: 'Barandales', singular: 'Barandal', grupo: 'Escaleras y rampas' },
  // --- Instalaciones (IfcDistributionElement y sus hijas) ---
  IfcDistributionElement: { plural: 'Instalaciones', singular: 'Pieza de instalación', grupo: 'Instalaciones' },
  IfcFlowSegment: { plural: 'Tramos (tubo, ducto, cable)', singular: 'Tramo', grupo: 'Instalaciones' },
  IfcPipeSegment: { plural: 'Tubería', singular: 'Tubo', grupo: 'Instalaciones' },
  IfcDuctSegment: { plural: 'Ductos', singular: 'Ducto', grupo: 'Instalaciones' },
  IfcCableSegment: { plural: 'Cables', singular: 'Cable', grupo: 'Instalaciones' },
  IfcCableCarrierSegment: { plural: 'Charolas y canalizaciones', singular: 'Charola', grupo: 'Instalaciones' },
  IfcFlowFitting: { plural: 'Conexiones', singular: 'Conexión', grupo: 'Instalaciones' },
  IfcPipeFitting: { plural: 'Conexiones de tubería', singular: 'Conexión', grupo: 'Instalaciones' },
  IfcDuctFitting: { plural: 'Conexiones de ducto', singular: 'Conexión', grupo: 'Instalaciones' },
  IfcFlowTerminal: { plural: 'Salidas y muebles de instalación', singular: 'Salida', grupo: 'Instalaciones' },
  IfcAirTerminal: { plural: 'Difusores y rejillas', singular: 'Difusor', grupo: 'Instalaciones' },
  IfcSanitaryTerminal: { plural: 'Muebles sanitarios', singular: 'Mueble sanitario', grupo: 'Instalaciones' },
  IfcLightFixture: { plural: 'Luminarias', singular: 'Luminaria', grupo: 'Instalaciones' },
  IfcOutlet: { plural: 'Contactos', singular: 'Contacto', grupo: 'Instalaciones' },
  IfcFlowController: { plural: 'Llaves y válvulas', singular: 'Llave o válvula', grupo: 'Instalaciones' },
  IfcValve: { plural: 'Válvulas', singular: 'Válvula', grupo: 'Instalaciones' },
  IfcFlowMovingDevice: { plural: 'Bombas y ventiladores', singular: 'Equipo', grupo: 'Instalaciones' },
  IfcEnergyConversionDevice: { plural: 'Equipos', singular: 'Equipo', grupo: 'Instalaciones' },
  IfcFlowStorageDevice: { plural: 'Tanques', singular: 'Tanque', grupo: 'Instalaciones' },
  IfcFlowTreatmentDevice: { plural: 'Filtros y tratamiento', singular: 'Filtro', grupo: 'Instalaciones' },
  IfcDistributionChamberElement: { plural: 'Registros', singular: 'Registro', grupo: 'Instalaciones' },
  IfcElectricAppliance: { plural: 'Aparatos eléctricos', singular: 'Aparato eléctrico', grupo: 'Instalaciones' },
  // --- Infraestructura (IFC 4.3) ---
  IfcCourse: { plural: 'Capas de pavimento y balasto', singular: 'Capa', grupo: 'Infraestructura' },
  IfcPavement: { plural: 'Pavimentos', singular: 'Pavimento', grupo: 'Infraestructura' },
  IfcKerb: { plural: 'Guarniciones', singular: 'Guarnición', grupo: 'Infraestructura' },
  IfcEarthworksElement: { plural: 'Terracerías', singular: 'Terracería', grupo: 'Infraestructura' },
  IfcEarthworksFill: { plural: 'Rellenos y terraplenes', singular: 'Relleno', grupo: 'Infraestructura' },
  IfcReinforcedSoil: { plural: 'Suelo reforzado', singular: 'Suelo reforzado', grupo: 'Infraestructura' },
  IfcTrackElement: { plural: 'Elementos de vía', singular: 'Elemento de vía', grupo: 'Infraestructura' },
  IfcRail: { plural: 'Rieles', singular: 'Riel', grupo: 'Infraestructura' },
  IfcBearing: { plural: 'Apoyos', singular: 'Apoyo', grupo: 'Infraestructura' },
  IfcSign: { plural: 'Señales', singular: 'Señal', grupo: 'Infraestructura' },
  IfcSignal: { plural: 'Semáforos y señales luminosas', singular: 'Señal luminosa', grupo: 'Infraestructura' },
  IfcSurfaceFeature: { plural: 'Marcas y tratamientos de superficie', singular: 'Marca de superficie', grupo: 'Infraestructura' },
  IfcImpactProtectionDevice: { plural: 'Defensas', singular: 'Defensa', grupo: 'Infraestructura' },
  IfcGeotechnicalElement: { plural: 'Elementos geotécnicos', singular: 'Elemento geotécnico', grupo: 'Infraestructura' },
  // --- Genéricos, mobiliario, entorno (no son obra por defecto: ver `decisionIfc`) ---
  IfcBuildingElementProxy: { plural: 'Elementos genéricos', singular: 'Elemento genérico', grupo: 'Otros' },
  IfcBuiltElementProxy: { plural: 'Elementos genéricos', singular: 'Elemento genérico', grupo: 'Otros' },
  IfcFurnishingElement: { plural: 'Mobiliario', singular: 'Mueble', grupo: 'Otros' },
  IfcFurniture: { plural: 'Mobiliario', singular: 'Mueble', grupo: 'Otros' },
  IfcSystemFurnitureElement: { plural: 'Mobiliario', singular: 'Mueble', grupo: 'Otros' },
  IfcGeographicElement: { plural: 'Terreno y entorno', singular: 'Elemento del entorno', grupo: 'Otros' },
  IfcTransportElement: { plural: 'Elevadores y transporte', singular: 'Elevador', grupo: 'Otros' },
  IfcElementComponent: { plural: 'Componentes', singular: 'Componente' },
  IfcOpeningElement: { plural: 'Huecos y vanos', singular: 'Hueco' },
  IfcSpace: { plural: 'Espacios', singular: 'Espacio' },
};

/** Busca una clase sin importar mayúsculas (web-ifc las nombra IFCWALL; IfcOpenShell, IfcWall). */
const INFO_POR_MAYUSCULAS = new Map(Object.entries(CLASES_IFC_INFO).map(([k, v]) => [k.toUpperCase(), v]));
const infoDe = (clase: string | null | undefined): ClaseIfcInfo | undefined =>
  clase ? INFO_POR_MAYUSCULAS.get(clase.toUpperCase()) : undefined;

/** La info de la clase o, si no está en la tabla, la de su ancestro más cercano que sí esté. */
function infoHeredada(
  clase: string | null | undefined,
  supertipos: readonly string[] | null | undefined,
  campo: 'grupo',
): ClaseIfcInfo | undefined {
  const propia = infoDe(clase);
  if (propia?.[campo] != null) return propia;
  for (const s of supertipos ?? []) {
    const i = infoDe(s);
    if (i?.[campo] != null) return i;
  }
  return undefined;
}

/** Nombre PLURAL de la clase (encabezados de agrupación). Derivado de la tabla de arriba. */
export const CATEGORIA_IFC_ES: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(CLASES_IFC_INFO).map(([k, v]) => [k, v.plural]),
);

/** Nombre SINGULAR de la clase ("Muro 1", "Puerta 2", "Muro exterior 30 cm"). */
export const CATEGORIA_IFC_ES_SINGULAR: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(CLASES_IFC_INFO).map(([k, v]) => [k, v.singular]),
);

/**
 * Nombre en español de la clase. Si la tabla no la conoce, su nombre IFC tal cual (IfcXxx): se ve y se
 * puede buscar en la norma, en vez de desaparecer o de inventarle un nombre.
 */
export function categoriaIfcEnEspanol(clase: string): string {
  return infoDe(clase)?.plural ?? clase;
}

export function categoriaIfcSingular(clase: string | null | undefined): string {
  if (!clase) return 'Pieza';
  return infoDe(clase)?.singular ?? clase;
}

/**
 * Grupo de un tipo por su clase, en este orden:
 *  1. la clase (o su ancestro más cercano en el esquema) que la tabla conoce: un tubo es Instalaciones
 *     por ser IfcDistributionElement, una varilla es Refuerzo por ser IfcReinforcingElement;
 *  2. en losas, el PredefinedType estándar ROOF la manda a "Techo";
 *  3. lo que no se conoce y está dentro de una instalación de infraestructura (IfcBridge, IfcRoad,
 *     IfcRailway, IfcMarineFacility: toda IfcFacility que no sea IfcBuilding) → "Infraestructura";
 *  4. lo demás → "Otros" (visible, con su nombre).
 */
export function grupoIfc(
  clase: string | null | undefined,
  predefinido?: string | null,
  supertipos?: readonly string[] | null,
  instalacion?: string | null,
): GrupoIfc {
  const g = infoHeredada(clase, supertipos, 'grupo')?.grupo;
  if (g === 'Losas' && (predefinido ?? '').toUpperCase() === 'ROOF') return 'Techo';
  if (g) return g;
  if (esInstalacionDeInfraestructura(instalacion)) return 'Infraestructura';
  return 'Otros';
}

/** El grupo de una capa IFC (con lo que el modelo dice de ella). */
export function grupoDeCapaIfc(c: Pick<PlanoCapa, 'categoria' | 'ifc'>): GrupoIfc {
  return grupoIfc(c.categoria, c.ifc?.predefinido, c.ifc?.supertipos, c.ifc?.instalacion);
}

/**
 * ¿La instalación (IfcFacility) es de infraestructura? Toda IfcFacility de IFC 4.3 que no sea un
 * edificio: puente, carretera, vía férrea, instalación marítima (o una nueva que agregue la norma).
 */
export function esInstalacionDeInfraestructura(instalacion: string | null | undefined): boolean {
  return !!instalacion && instalacion.toUpperCase() !== 'IFCBUILDING';
}

/**
 * Decisión por default de un tipo IFC por su CLASE estándar. El IFC no pasa por el clasificador de
 * capas por IA (ya viene tipado):
 *  - mobiliario, anotaciones, espacios, ejes, vanos y elementos virtuales → "Es solo dibujo";
 *  - los GENÉRICOS (IfcBuildingElementProxy / IfcBuiltElementProxy) y el entorno (IfcGeographicElement)
 *    → "Revisar": el modelo no dice qué son; ahí caen las personas, árboles y autos que exportan los
 *    renderizadores, pero también obra modelada sin clase. No entran a "Se construye" solos; el usuario
 *    los mueve con el ⋯;
 *  - lo demás es obra.
 * Por la clase y sus ancestros (la herencia del esquema), no por el nombre del tipo.
 */
export function decisionIfc(clase: string | null | undefined, supertipos?: readonly string[] | null): DecisionDeCapa {
  const todas = [clase ?? '', ...(supertipos ?? [])].map((s) => s.toUpperCase());
  const es = (c: string) => todas.includes(c.toUpperCase());
  if (
    es('IfcFurnishingElement') ||
    es('IfcAnnotation') ||
    es('IfcSpatialElement') ||
    es('IfcSpace') ||
    es('IfcGrid') ||
    es('IfcFeatureElementSubtraction') ||
    es('IfcVirtualElement')
  ) {
    return 'ruido';
  }
  if (es('IfcBuildingElementProxy') || es('IfcBuiltElementProxy') || es('IfcGeographicElement')) return 'dudoso';
  return 'cotiza';
}

/**
 * ¿Tiene sentido mostrar el LARGO de un tipo? No si se cuenta por pieza (`manda`): la suma de los
 * "largos" de 5 puertas es la de sus alturas o anchos y no mide obra. Sin `manda` (inventario viejo),
 * el criterio de antes: todo salvo puertas y ventanas.
 */
export function muestraLargoIfc(
  clase: string | null | undefined,
  _supertipos?: readonly string[] | null,
  manda?: MagnitudIfc | null,
): boolean {
  if (manda) return manda !== 'conteo';
  return clase !== 'IfcDoor' && clase !== 'IfcWindow';
}

type Medidas = NonNullable<PlanoCapa['medidas']>;

/**
 * Espesor MEDIDO de un muro o una losa: volumen ÷ área del propio modelo (en metros). Solo cuando las
 * dos magnitudes las declara el modelo (si una se estimó de la malla, el cociente no es confiable).
 */
export function espesorMedidoIfc(capa: Pick<PlanoCapa, 'categoria' | 'medidas' | 'ifc'>): number | null {
  const g = grupoDeCapaIfc(capa);
  if (g !== 'Muros' && g !== 'Losas' && g !== 'Techo') return null;
  const m = capa.medidas;
  if (!m || !(m.area && m.area > 0) || !(m.volumen && m.volumen > 0)) return null;
  const est = m.magnitudesEstimadas ?? [];
  if (est.includes('area') || est.includes('volumen')) return null;
  const e = m.volumen / m.area;
  return Number.isFinite(e) && e > 0 ? e : null;
}

function fmtCm(m: number): string {
  return `${Math.round(m * 100)} cm`;
}

/**
 * Nombre legible del tipo: clase en singular + exterior/interior (IsExternal del modelo) + espesor
 * medido. "Muro exterior 30 cm", "Losa 20 cm", "Ventana". Sin datos del modelo, solo la clase.
 */
export function nombreLegibleTipoIfc(capa: Pick<PlanoCapa, 'categoria' | 'medidas' | 'ifc'>): string {
  const partes = [categoriaIfcSingular(capa.categoria)];
  const g = grupoDeCapaIfc(capa);
  if (g === 'Muros' && capa.ifc?.esExterior != null) partes.push(capa.ifc.esExterior ? 'exterior' : 'interior');
  const e = espesorMedidoIfc(capa);
  if (e != null) partes.push(fmtCm(e));
  return partes.join(' ');
}


/**
 * El nombre del tipo TAL COMO LO DECLARA EL MODELO, partido en familia y tipo cuando viene como
 * «Familia:Tipo» (así lo exportan Revit y otros: «Suelo:FIRME CONCRETO»). Es el nombre que la persona
 * puso en su programa, así que es el que debe ver para reconocer el tipo. null si el modelo no nombra
 * el tipo (el nombre salió de la clase). No traduce ni corrige: muestra lo que el modelo dice.
 */
export function nombreDeclaradoDelTipoIfc(
  capa: Pick<PlanoCapa, 'tipoDelModelo'>,
): { tipo: string; familia: string | null } | null {
  const t = capa.tipoDelModelo;
  if (!t || t.origen === 'clase') return null;
  const nombre = t.nombre.trim();
  const i = nombre.indexOf(':');
  if (i > 0 && i < nombre.length - 1) return { familia: nombre.slice(0, i).trim(), tipo: nombre.slice(i + 1).trim() };
  return nombre ? { tipo: nombre, familia: null } : null;
}


export interface MedidaPrincipalIfc {
  magnitud: MagnitudIfc;
  valor: number;
  unidad: 'm²' | 'm' | 'm³' | 'pza';
  /** La magnitud se estimó de la forma de la pieza (el modelo no la declara). */
  estimada: boolean;
}

const UNIDAD: Record<MagnitudIfc, MedidaPrincipalIfc['unidad']> = { area: 'm²', longitud: 'm', volumen: 'm³', conteo: 'pza' };

/**
 * La cantidad que "manda" en un tipo, para la lista y para ordenar lo que falta: la que decidió el visor
 * (`medidas.manda`, por la forma de las piezas: una zapata va por m³, un tubo por m, un vidrio por m²,
 * una puerta por pieza), si el tipo la trae medida; si no, por pieza. Los otros parámetros quedan por
 * compatibilidad con los llamadores (no cambian la magnitud).
 */
export function medidaPrincipalIfc(
  medidas: Pick<Medidas, 'conteo' | 'area' | 'longitud' | 'volumen' | 'magnitudesEstimadas' | 'manda'> | null | undefined,
  _clase?: string | null,
  _predefinido?: string | null,
  _supertipos?: readonly string[] | null,
): MedidaPrincipalIfc | null {
  if (!medidas) return null;
  const orden: readonly MagnitudIfc[] = medidas.manda && medidas.manda !== 'conteo' ? [medidas.manda, 'conteo'] : ['conteo'];
  const est = medidas.magnitudesEstimadas ?? [];
  for (const mag of orden) {
    const v = mag === 'conteo' ? medidas.conteo : medidas[mag];
    if (v != null && v > 0) {
      return { magnitud: mag, valor: v, unidad: UNIDAD[mag], estimada: mag !== 'conteo' && est.includes(mag) };
    }
  }
  return null;
}

const NUM = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** "136.28 m²", "9 pza". */
export function fmtMedidaIfc(m: Pick<MedidaPrincipalIfc, 'valor' | 'unidad'>): string {
  return `${NUM.format(m.valor)} ${m.unidad}`;
}

/**
 * Una pieza como la entiende una persona: "Muro 3 · Planta baja · 10.43 m²" (nombre o marca del
 * modelo si la trae; si no, la clase + su número), nunca el GUID ni el expressID.
 */
/**
 * ¿El "nombre" es en realidad un identificador de máquina (un GUID o el GlobalId IFC de
 * 22 caracteres)? Eso no le dice nada a una persona: se usa la clase + su número.
 */
export function pareceIdentificadorIfc(nombre: string): boolean {
  const s = nombre.trim();
  // GUID con guiones en cualquier agrupación (ArchiCAD escribe 8-4-4-2-2-12) o GlobalId IFC.
  return /^[0-9a-f]{32}$/i.test(s.replace(/-/g, '')) || /^[0-9A-Za-z_$]{22}$/.test(s);
}

export function nombrePiezaIfc(p: {
  nombre?: string | null;
  clase: string | null | undefined;
  indice: number;
  nivel?: string | null;
  medida?: Pick<MedidaPrincipalIfc, 'valor' | 'unidad'> | null;
}): string {
  const propio = p.nombre?.trim() && !pareceIdentificadorIfc(p.nombre) ? p.nombre.trim() : null;
  const partes = [propio || `${categoriaIfcSingular(p.clase)} ${p.indice + 1}`];
  if (p.nivel && p.nivel.trim()) partes.push(p.nivel.trim());
  if (p.medida) partes.push(fmtMedidaIfc(p.medida));
  return partes.join(' · ');
}

/**
 * Lo que se le cuenta a la IA de un tipo IFC al pedir sugerencias de concepto. El nombre del tipo
 * puede venir en otro idioma o ser un código del programa que lo exportó: lo que MANDA es la clase
 * IFC, y se completa con lo que el modelo declara (predefinido, materiales, exterior) y lo medido.
 */
export interface PlanoSugerenciaIfc {
  clase: string;
  predefinido?: string | null;
  /** Etiqueta en español armada con esta misma fuente ("Muro exterior 30 cm"). */
  etiqueta: string;
  materiales?: string[];
  /** Espesor medido (m). */
  espesorM?: number | null;
  esExterior?: boolean | null;
  cantidad?: { valor: number; unidad: MedidaPrincipalIfc['unidad'] } | null;
}

export function describirTipoIfcParaSugerencia(capa: PlanoCapa): PlanoSugerenciaIfc | null {
  if (!capa.categoria) return null;
  const principal = medidaPrincipalIfc(capa.medidas, capa.categoria, capa.ifc?.predefinido, capa.ifc?.supertipos);
  return {
    clase: capa.categoria,
    predefinido: capa.ifc?.predefinido ?? null,
    etiqueta: nombreLegibleTipoIfc(capa),
    materiales: capa.ifc?.materiales?.length ? capa.ifc.materiales : undefined,
    espesorM: espesorMedidoIfc(capa),
    esExterior: capa.ifc?.esExterior ?? null,
    cantidad: principal ? { valor: principal.valor, unidad: principal.unidad } : null,
  };
}

/**
 * El siguiente tipo que falta presupuestar ("Empieza por lo grande" / "Siguiente que falta"): de los
 * que no tienen concepto principal, el que más pesa en el modelo. Solo tipos con algo medido.
 */
export function siguienteTipoQueFalta(
  capas: readonly PlanoCapa[],
  conConcepto: ReadonlySet<string>,
  excluir?: (capa: PlanoCapa) => boolean,
): { capa: PlanoCapa; medida: MedidaPrincipalIfc } | null {
  // Criterio: el VOLUMEN total del tipo (lo que más material lleva), la única medida que se compara
  // entre un muro (m²), una viga (m) y un mueble (pza); sin volumen medido, después, por su medida que manda.
  const rango: Record<MagnitudIfc, number> = { area: 0, longitud: 1, volumen: 2, conteo: 3 };
  let mejor: { capa: PlanoCapa; medida: MedidaPrincipalIfc; vol: number } | null = null;
  for (const c of capas) {
    if (!c.categoria || conConcepto.has(c.capa) || excluir?.(c)) continue;
    const m = medidaPrincipalIfc(c.medidas, c.categoria, c.ifc?.predefinido, c.ifc?.supertipos);
    if (!m) continue;
    const vol = c.medidas?.volumen ?? 0;
    if (
      !mejor ||
      vol > mejor.vol ||
      (vol === mejor.vol &&
        (rango[m.magnitud] < rango[mejor.medida.magnitud] ||
          (rango[m.magnitud] === rango[mejor.medida.magnitud] && m.valor > mejor.medida.valor)))
    ) {
      mejor = { capa: c, medida: m, vol };
    }
  }
  return mejor ? { capa: mejor.capa, medida: mejor.medida } : null;
}
